import { RootEntry } from "@/app/root-entry";
import { WorkspaceShell } from "@/app/workspace-shell";
import { validateStatisticsPageSearch } from "@/features/statistics/statistics-model";
import { NotFoundPage } from "@/pages/not-found-page";
import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
} from "@tanstack/react-router";

type RouterContext = { queryClient: QueryClient };

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: RootEntry,
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: lazyRouteComponent(() => import("@/pages/login-page"), "LoginPage"),
});
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invite",
  component: lazyRouteComponent(() => import("@/pages/accept-invite-page"), "AcceptInvitePage"),
});
const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceSlug",
  component: WorkspaceShell,
});
const workspaceIndexRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/",
  component: lazyRouteComponent(() => import("@/pages/workspace-index-page"), "WorkspaceIndexPage"),
});
const onboardingRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/onboarding",
  component: lazyRouteComponent(() => import("@/pages/onboarding-page"), "OnboardingPage"),
});
const todayRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/today",
  component: lazyRouteComponent(() => import("@/pages/today-page"), "TodayPage"),
});
/** "Programmati" and "Memo" in the calendar stay in the URL (`?view=scheduled`, `?view=memo`). */
export type CalendarSearch = { view?: "scheduled" | "memo" };
const calendarRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/calendar",
  validateSearch: (search: Record<string, unknown>): CalendarSearch =>
    search.view === "scheduled" || search.view === "memo" ? { view: search.view } : {},
  component: lazyRouteComponent(() => import("@/pages/calendar-page"), "CalendarPage"),
});
/** Search, stage and assignee of the list stay in the URL while chats open and close. */
export type InboxSearch = {
  q?: string;
  stage?: string;
  assignee?: string;
  turn?: "me";
  /** Posta → Canale: WhatsApp, Instagram or Messenger (ADR 0092). */
  channel?: string;
  /** From Statistiche: leads we never wrote to, or who never answered us (T-PL-40). */
  reply?: "none" | "never";
};
function inboxSearch(search: Record<string, unknown>): InboxSearch {
  return {
    ...(search.reply === "none" || search.reply === "never" ? { reply: search.reply } : {}),
    ...(typeof search.q === "string" && search.q ? { q: search.q } : {}),
    ...(typeof search.stage === "string" && search.stage ? { stage: search.stage } : {}),
    ...(typeof search.assignee === "string" && search.assignee
      ? { assignee: search.assignee }
      : {}),
    ...(search.turn === "me" ? { turn: "me" as const } : {}),
    ...(typeof search.channel === "string" && search.channel ? { channel: search.channel } : {}),
  };
}
const inboxRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/inbox",
  validateSearch: inboxSearch,
  component: lazyRouteComponent(() => import("@/pages/inbox-page"), "InboxPage"),
});
const inboxLeadRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/inbox/$leadId",
  validateSearch: inboxSearch,
  component: lazyRouteComponent(() => import("@/pages/inbox-page"), "InboxPage"),
});
const leadsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/leads",
  component: lazyRouteComponent(() => import("@/pages/leads-page"), "LeadsPage"),
});
const customersRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/customers",
  component: lazyRouteComponent(() => import("@/pages/leads-page"), "CustomerCarePage"),
});
/** The selected chat and personal queue filters survive opening and closing the panel. */
export type ApprovalsSearch = { lead?: string; assignee?: string; filter?: string };
const approvalsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/approvals",
  validateSearch: (search: Record<string, unknown>): ApprovalsSearch => ({
    ...(typeof search.lead === "string" && search.lead ? { lead: search.lead } : {}),
    ...(typeof search.assignee === "string" && search.assignee
      ? { assignee: search.assignee }
      : {}),
    ...(typeof search.filter === "string" && search.filter ? { filter: search.filter } : {}),
  }),
  component: lazyRouteComponent(() => import("@/pages/approvals-page"), "ApprovalsPage"),
});
const templatesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/templates",
  component: lazyRouteComponent(() => import("@/pages/templates-page"), "TemplatesPage"),
});
const wikiRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/wiki",
  validateSearch: (search: Record<string, unknown>): { page?: string } =>
    typeof search.page === "string" && search.page ? { page: search.page } : {},
  component: lazyRouteComponent(() => import("@/pages/wiki-page"), "WikiPage"),
});
const costsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/costs",
  component: lazyRouteComponent(() => import("@/pages/costs-page"), "CostsPage"),
});
const statisticsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/statistics",
  validateSearch: validateStatisticsPageSearch,
  component: lazyRouteComponent(() => import("@/pages/statistics-page"), "StatisticsPage"),
});
const integrationsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/integrations",
  component: lazyRouteComponent(() => import("@/pages/integrations-page"), "IntegrationsPage"),
});
const teamRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/team",
  component: lazyRouteComponent(() => import("@/pages/team-page"), "TeamPage"),
});
const settingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings",
  component: lazyRouteComponent(() => import("@/pages/settings-page"), "SettingsPage"),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  inviteRoute,
  workspaceRoute.addChildren([
    workspaceIndexRoute,
    onboardingRoute,
    todayRoute,
    calendarRoute,
    inboxRoute,
    inboxLeadRoute,
    leadsRoute,
    customersRoute,
    approvalsRoute,
    templatesRoute,
    wikiRoute,
    costsRoute,
    statisticsRoute,
    integrationsRoute,
    teamRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient: undefined as unknown as QueryClient },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
